import type { AnalyticsMapping, AnalyticsTable } from '../../services/analytics';
import { DASH, formatInt, formatPct } from './format';
import type { MetricInfoKey } from './metricInfo';

export interface MappingCardView {
    id: MetricInfoKey;
    title: string;
    value: string;
    caption: string;
    highlight?: boolean;
}

const contas = (n: number) => `${formatInt(n)} ${n === 1 ? 'conta' : 'contas'}`;

/** Cards do topo do Mapeamento de clientes, como no prototipo. */
export function buildMappingCards(m: AnalyticsMapping): MappingCardView[] {
    const c = m.cards;
    const topSetor = m.setorAgrupado[0];
    const topControle = m.comoControla[0];
    return [
        { id: 'contasCriadas', title: 'Contas criadas no período', value: formatInt(c.contasCriadas), caption: 'Safra analisada', highlight: true },
        {
            id: 'responderam',
            title: 'Responderam',
            value: formatInt(c.responderam.count),
            caption: c.responderam.pct === null ? 'Sem contas na safra' : `${formatPct(c.responderam.pct)} da base`,
        },
        { id: 'semResposta', title: 'Sem resposta', value: formatInt(c.semResposta), caption: 'Sem perfil declarado' },
        {
            id: 'setorMaisFrequente',
            title: 'Setor mais frequente',
            value: c.setorMaisFrequente ?? DASH,
            caption: topSetor ? contas(topSetor.count) : 'Sem respostas',
        },
        {
            id: 'controleMaisComum',
            title: 'Controle mais comum',
            value: c.controleMaisComum ?? DASH,
            caption: topControle ? contas(topControle.count) : 'Sem respostas',
        },
    ];
}

/** Barra de uma distribuicao: rotulo, contagem e percentual das respondentes. */
export interface DistributionBar {
    label: string;
    count: number;
    pct: number | null;
}

export const setorBars = (m: AnalyticsMapping): DistributionBar[] =>
    m.setorAgrupado.map((s) => ({ label: s.setor, count: s.count, pct: s.pct }));

/** Tabela local de "Ver dados e exportar" de uma distribuicao. */
export function distributionTable(name: string, bars: DistributionBar[]): AnalyticsTable {
    return {
        name,
        columns: [
            { key: 'categoria', label: 'Categoria' },
            { key: 'contas', label: 'Contas' },
            { key: 'pct', label: '% das respondentes' },
        ],
        rows: bars.map((b) => ({ categoria: b.label, contas: b.count, pct: b.pct })),
    };
}

/** Grupo comparado em percentual (Setor x comportamento, Controle x ativacao). */
export interface GroupSeries {
    key: string;
    label: string;
}

export interface GroupRow {
    group: string;
    respondentes: number;
    values: Record<string, number | null>;
    counts: Record<string, number>;
}

export const SETOR_SERIES: GroupSeries[] = [
    { key: 'pagantes', label: 'Pagantes' },
    { key: 'crfaNoGrupo', label: 'CRFA no grupo' },
    { key: 'ausentes', label: 'Ausentes' },
];

export const CONTROLE_SERIES: GroupSeries[] = [
    { key: 'ativadas', label: 'Ativadas' },
    { key: 'crfaNoGrupo', label: 'CRFA no grupo' },
];

export const setorRows = (m: AnalyticsMapping): GroupRow[] =>
    m.setorXComportamento.map((s) => ({
        group: s.setor,
        respondentes: s.respondentes,
        values: { pagantes: s.pagantes, crfaNoGrupo: s.crfaNoGrupo, ausentes: s.ausentes },
        counts: s.contagem,
    }));

export const controleRows = (m: AnalyticsMapping): GroupRow[] =>
    m.controleXAtivacao.map((c) => ({
        group: c.label,
        respondentes: c.respondentes,
        values: { pagantes: c.pagantes, ativadas: c.ativadas, crfaNoGrupo: c.crfaNoGrupo },
        counts: c.contagem,
    }));

/** Colunas da tabela de uma comparacao: primeiro as contagens, depois os percentuais do grupo. */
export interface GroupTableSpec {
    counts: Array<{ key: string; label: string }>;
    pcts: Array<{ key: string; label: string }>;
}

/** Setor x comportamento, como no prototipo. */
export const SETOR_TABLE: GroupTableSpec = {
    counts: [
        { key: 'pagantes', label: 'Pagantes' },
        { key: 'crfaNoGrupo', label: 'CRFA' },
        { key: 'ausentes', label: 'Ausentes' },
    ],
    pcts: [
        { key: 'pagantes', label: 'Pagantes / grupo' },
        { key: 'crfaNoGrupo', label: 'CRFA / grupo' },
        { key: 'ausentes', label: 'Ausentes / grupo' },
    ],
};

/** Controle informado x ativacao, como no prototipo (Pagantes so em contagem). */
export const CONTROLE_TABLE: GroupTableSpec = {
    counts: [
        { key: 'pagantes', label: 'Pagantes' },
        { key: 'ativadas', label: 'Ativadas' },
        { key: 'crfaNoGrupo', label: 'CRFA' },
    ],
    pcts: [
        { key: 'ativadas', label: 'Ativadas / grupo' },
        { key: 'crfaNoGrupo', label: 'CRFA / grupo' },
    ],
};

/** Tabela local de "Ver dados e exportar" de uma comparacao por grupo. */
export function groupTable(name: string, groupLabel: string, spec: GroupTableSpec, rows: GroupRow[]): AnalyticsTable {
    return {
        name,
        columns: [
            { key: 'grupo', label: groupLabel },
            { key: 'respondentes', label: 'Respondentes' },
            ...spec.counts.map((c) => ({ key: `${c.key}N`, label: c.label })),
            ...spec.pcts.map((c) => ({ key: `${c.key}Pct`, label: c.label })),
        ],
        rows: rows.map((r) => {
            const row: Record<string, unknown> = { grupo: r.group, respondentes: r.respondentes };
            for (const c of spec.counts) row[`${c.key}N`] = r.counts[c.key] ?? 0;
            for (const c of spec.pcts) row[`${c.key}Pct`] = r.values[c.key];
            return row;
        }),
    };
}
